import React, { useState, useEffect } from 'react';
import { ArrowLeft, Edit2, Download, Printer, Paperclip, Upload, Send, Clock, MessageSquare } from 'lucide-react';
import ApprovalStatusBadge from '../ui/ApprovalStatusBadge';
import { DocumentTrail } from '../ui/DocumentTrail';
import RevisionHistory from '../ui/RevisionHistory';
import { purchaseAPI } from '../../services/api';
import { toast } from 'sonner';

const statusColors = {
  'Draft': 'bg-gray-100 text-gray-600',
  'Ordered': 'bg-amber-100 text-amber-700',
  'In Transit': 'bg-blue-100 text-blue-700',
  'Partially Received': 'bg-purple-100 text-purple-700',
  'Received': 'bg-green-100 text-green-700',
  'Cancelled': 'bg-red-100 text-red-700',
};

const PurchaseOrderDetail = ({
  selectedPO, user, uploading, setUploading,
  onBack, onEdit, generatePDF, printPO, updatePOStatus,
  confirmDraftPO, canConfirmPO, canEdit = true,
  fileAPI, fetchAll, purchaseAPI: purchaseAPIProp, setSelectedPO,
}) => {
  const isImport = selectedPO.type === 'import';
  const currency = selectedPO.currency || 'INR';
  const currencySymbol = currency === 'INR' ? '₹' : currency;

  // Updates state
  const [updateText, setUpdateText] = useState('');
  const [updates, setUpdates] = useState(selectedPO.updates || []);
  const [submittingUpdate, setSubmittingUpdate] = useState(false);

  useEffect(() => {
    setUpdates(selectedPO.updates || []);
  }, [selectedPO]);

  const handleAddUpdate = async () => {
    if (!updateText.trim()) return;
    try {
      setSubmittingUpdate(true);
      const result = await purchaseAPI.addUpdate(selectedPO.id, updateText.trim());
      setUpdates(prev => [...prev, result.update]);
      setUpdateText('');
      toast.success('Update added');
    } catch {
      toast.error('Failed to add update');
    } finally {
      setSubmittingUpdate(false);
    }
  };

  // Totals
  const itemsSubtotal = (selectedPO.items || []).reduce((s, i) => s + (i.amount || 0), 0);
  const otherChargesTotal = (selectedPO.other_charges || []).reduce((s, c) => s + (c.amount || 0), 0);
  const totalAmount = itemsSubtotal + otherChargesTotal;

  return (
    <div className="p-5 lg:p-6" data-testid="po-detail">
      <button onClick={onBack} className="flex items-center gap-1 text-xs font-medium text-gray-600 hover:text-gray-900 mb-4">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to List
      </button>

      <div className="bg-white rounded-xl border border-gray-100">
        <div className="p-5 border-b border-gray-100">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-base font-bold" style={{ fontFamily: 'DM Sans' }}>Purchase Order</h2>
              <div className="flex items-center gap-2 mt-0.5">
                <p className="text-xs text-gray-500 font-mono">{selectedPO.po_number || selectedPO.id}</p>
                {(selectedPO.revision_number || 0) > 0 && (
                  <span className="px-1.5 py-0.5 text-[9px] font-bold rounded-full bg-purple-100 text-purple-700" data-testid="po-revision-badge">Rev-{selectedPO.revision_number}</span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className={`inline-flex px-2.5 py-1 text-[10px] font-semibold rounded-full ${statusColors[selectedPO.status] || 'bg-gray-100'}`}>{selectedPO.status}</span>
              {canEdit && (
                <button data-testid="edit-po-btn" onClick={() => onEdit(selectedPO)} className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 flex items-center gap-1"><Edit2 className="w-3 h-3" /> Edit</button>
              )}
              <button data-testid="download-po-btn" onClick={() => generatePDF(selectedPO)} className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 flex items-center gap-1"><Download className="w-3 h-3" /> PDF</button>
              <button data-testid="print-po-btn" onClick={() => printPO(selectedPO)} className="px-3 py-1.5 text-xs font-semibold text-white rounded-lg flex items-center gap-1" style={{ backgroundColor: '#24549c' }}><Printer className="w-3 h-3" /> Print</button>
            </div>
          </div>
        </div>

        <div className="p-5 space-y-4">
          <ApprovalStatusBadge documentType="PO" documentId={selectedPO.id} documentNumber={selectedPO.po_number || selectedPO.id} onStatusChange={() => fetchAll()} />
          <DocumentTrail docType="po" docId={selectedPO.id} />

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'Date', value: selectedPO.date },
              { label: 'Type', value: (selectedPO.type || '').toUpperCase() },
              { label: 'Delivery Date', value: selectedPO.expected_delivery },
              { label: 'Reference', value: selectedPO.reference_no || '-' },
              ...(selectedPO.document_title ? [{ label: 'Document Title', value: selectedPO.document_title }] : []),
              ...(selectedPO.vendor_pi ? [{ label: 'Vendor PI', value: selectedPO.vendor_pi }] : []),
            ].map((f, i) => (
              <div key={i}><p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">{f.label}</p><p className="text-sm font-medium text-gray-900">{f.value}</p></div>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1 font-semibold" style={{ color: '#24549c' }}>Vendor</p>
              <p className="text-sm font-semibold text-gray-900">{selectedPO.vendor_name}</p>
              {(() => {
                let addr = selectedPO.vendor_address || '';
                if (selectedPO.vendor_city && !addr.includes(selectedPO.vendor_city)) addr += (addr ? ', ' : '') + selectedPO.vendor_city;
                if (selectedPO.vendor_state && !addr.includes(selectedPO.vendor_state)) addr += (addr ? ', ' : '') + selectedPO.vendor_state;
                return addr ? <p className="text-[10px] text-gray-600 mt-1 whitespace-pre-line">{addr}</p> : null;
              })()}
              {selectedPO.vendor_gst && <p className="text-[10px] text-gray-500 mt-1">GSTIN: {selectedPO.vendor_gst}</p>}
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1 font-semibold" style={{ color: '#24549c' }}>Bill To</p>
              <p className="text-xs text-gray-700 whitespace-pre-line">{selectedPO.billing_address || 'Sharda Infrasolutions Private Limited'}</p>
              {selectedPO.company_gst && <p className="text-[10px] text-gray-500 mt-1">GSTIN: {selectedPO.company_gst}</p>}
            </div>
            {selectedPO.shipping_address && (
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1 font-semibold" style={{ color: '#24549c' }}>Ship To</p>
                <p className="text-xs text-gray-700">{selectedPO.shipping_address}</p>
              </div>
            )}
          </div>

          {/* Items Table */}
          <div className="border border-gray-100 rounded-lg overflow-hidden">
            <table className="w-full text-xs">
              <thead><tr className="bg-gray-50">
                {['#', 'Product', 'HSN', 'Qty', 'Rate', ...(isImport ? [] : ['GST']), 'Amount'].map(h =>
                  <th key={h} className="text-left px-3 py-2 text-[10px] font-semibold text-gray-500">{h}</th>
                )}
              </tr></thead>
              <tbody>
                {(selectedPO.items || []).map((item, idx) => (
                  <tr key={idx} className="border-t border-gray-50">
                    <td className="px-3 py-2">{idx + 1}</td>
                    <td className="px-3 py-2">
                      <span className="font-medium">{item.name}</span>
                      {item.description && <p className="text-[10px] text-gray-400 mt-0.5">{item.description}</p>}
                    </td>
                    <td className="px-3 py-2 text-gray-500">{item.hsn_code || '-'}</td>
                    <td className="px-3 py-2">{item.qty} {item.unit}</td>
                    <td className="px-3 py-2">{currencySymbol} {(item.rate || 0).toLocaleString()}</td>
                    {!isImport && <td className="px-3 py-2">{item.gst_rate || 18}%</td>}
                    <td className="px-3 py-2 font-semibold">{currencySymbol} {(item.amount || 0).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Other Charges */}
          {(selectedPO.other_charges || []).length > 0 && (
            <div className="border border-gray-100 rounded-lg p-3" data-testid="po-detail-other-charges">
              <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2">Other Charges</p>
              <div className="space-y-1">
                {(selectedPO.other_charges || []).map((charge, idx) => (
                  <div key={idx} className="flex items-center justify-between text-xs">
                    <span className="text-gray-700">{charge.description || `Charge ${idx + 1}`} {!isImport && charge.taxable ? <span className="text-[9px] text-blue-500">(+GST)</span> : ''}</span>
                    <span className="font-semibold text-gray-900">{currencySymbol} {(charge.amount || 0).toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Totals */}
          <div className="flex justify-end">
            <div className="bg-gray-50 rounded-lg px-5 py-3 space-y-1 text-right">
              <p className="text-xs text-gray-500">Items Subtotal: <span className="font-semibold text-gray-900">{currencySymbol} {itemsSubtotal.toLocaleString()}</span></p>
              {otherChargesTotal > 0 && <p className="text-xs text-gray-500">Other Charges: <span className="font-semibold text-gray-900">{currencySymbol} {otherChargesTotal.toLocaleString()}</span></p>}
              {isImport ? (
                <>
                  <p className="text-sm font-bold pt-1 border-t border-gray-200" style={{ color: '#24549c' }}>
                    Total: {currencySymbol} {totalAmount.toLocaleString()}
                  </p>
                  {currency !== 'INR' && selectedPO.exchange_rate && (
                    <p className="text-[10px] text-gray-400">
                      INR {Math.round(totalAmount * (selectedPO.exchange_rate || 1)).toLocaleString()} (@ {selectedPO.exchange_rate} per {currency})
                    </p>
                  )}
                </>
              ) : (
                <>
                  {(() => {
                    const taxableChargesTotal = (selectedPO.other_charges || []).filter(c => c.taxable).reduce((s, c) => s + (c.amount || 0), 0);
                    const nonTaxableChargesTotal = (selectedPO.other_charges || []).filter(c => !c.taxable).reduce((s, c) => s + (c.amount || 0), 0);
                    // Per-item GST calculation
                    const perItemGST = (selectedPO.items || []).reduce((sum, item) => sum + (item.amount || 0) * ((item.gst_rate ?? 18) / 100), 0);
                    const weightedAvgRate = itemsSubtotal > 0
                      ? (selectedPO.items || []).reduce((s, i) => s + (i.amount || 0) * ((i.gst_rate ?? 18) / 100), 0) / itemsSubtotal * 100
                      : 18;
                    const chargesGST = taxableChargesTotal * (weightedAvgRate / 100);
                    const gstAmt = perItemGST + chargesGST;
                    const grandTotal = itemsSubtotal + taxableChargesTotal + nonTaxableChargesTotal + gstAmt;
                    // Dynamic GST label
                    const gstRates = [...new Set((selectedPO.items || []).map(i => i.gst_rate ?? 18))];
                    const gstLabel = gstRates.length === 1 ? `GST (${gstRates[0]}%)` : 'GST (Mixed Rates)';
                    return (
                      <>
                        <p className="text-xs text-gray-500">{gstLabel}: <span className="font-semibold text-gray-900">INR {Math.round(gstAmt).toLocaleString()}</span></p>
                        <p className="text-sm font-bold pt-1 border-t border-gray-200" style={{ color: '#24549c' }}>Grand Total: INR {Math.round(grandTotal).toLocaleString()}</p>
                      </>
                    );
                  })()}
                </>
              )}
            </div>
          </div>

          {/* Terms & Notes */}
          {(selectedPO.payment_terms || selectedPO.delivery_terms || selectedPO.notes || selectedPO.comments) && (
            <div className="bg-gray-50 rounded-lg p-3 space-y-1">
              {selectedPO.payment_terms && <p className="text-xs"><span className="font-semibold" style={{ color: '#24549c' }}>Payment Terms:</span> {selectedPO.payment_terms}</p>}
              {selectedPO.delivery_terms && <p className="text-xs"><span className="font-semibold" style={{ color: '#24549c' }}>Delivery Terms:</span> {selectedPO.delivery_terms}</p>}
              {selectedPO.comments && <p className="text-xs"><span className="font-semibold" style={{ color: '#24549c' }}>Comments:</span> {selectedPO.comments}</p>}
              {selectedPO.notes && <p className="text-xs"><span className="font-semibold" style={{ color: '#24549c' }}>Notes:</span> {selectedPO.notes}</p>}
            </div>
          )}

          {/* Shipping Details (Import) */}
          {selectedPO.type === 'import' && (selectedPO.loading_port || selectedPO.discharge_port || selectedPO.incoterms || selectedPO.dispatch_by || selectedPO.etd || selectedPO.eta || selectedPO.shipping_line || selectedPO.bl_awb_number) && (
            <div className="border border-blue-100 bg-blue-50/30 rounded-lg p-3">
              <p className="text-[10px] font-bold text-blue-800 uppercase tracking-wider mb-2">Import / Shipping Details</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { label: 'Loading Port', value: selectedPO.loading_port },
                  { label: 'Discharge Port', value: selectedPO.discharge_port },
                  { label: 'Dispatch By', value: selectedPO.dispatch_by },
                  { label: 'Incoterms', value: selectedPO.incoterms },
                  { label: 'ETD', value: selectedPO.etd },
                  { label: 'ETA', value: selectedPO.eta },
                  { label: 'Shipping Line', value: selectedPO.shipping_line },
                  { label: 'BL/AWB No.', value: selectedPO.bl_awb_number },
                ].filter(f => f.value && f.value !== 'None').map((f, i) => (
                  <div key={i}><p className="text-[9px] text-gray-500 uppercase">{f.label}</p><p className="text-xs font-medium text-gray-900">{f.value}</p></div>
                ))}
              </div>
            </div>
          )}

          {/* Attachments */}
          <div className="border border-gray-100 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-gray-900 flex items-center gap-1"><Paperclip className="w-3 h-3" /> Attachments ({(selectedPO.attachments || []).length})</p>
              <label className="flex items-center gap-1 px-3 py-1.5 text-[10px] font-semibold rounded-md cursor-pointer hover:bg-blue-50" style={{ color: '#24549c' }}>
                <Upload className="w-3 h-3" /> {uploading ? 'Uploading...' : 'Upload'}
                <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx" disabled={uploading} onChange={async (e) => {
                  const file = e.target.files[0]; if (!file) return;
                  try { setUploading(true); await fileAPI.upload(file, 'po', selectedPO.id); fetchAll(); const updated = await purchaseAPIProp.getAll(); const po = updated.find(p => p.id === selectedPO.id); if (po) setSelectedPO(po); } catch { alert('Upload failed'); }
                  finally { setUploading(false); e.target.value = ''; }
                }} />
              </label>
            </div>
            {(selectedPO.attachments || []).length > 0 ? (
              <div className="space-y-1">
                {selectedPO.attachments.map((att, i) => (
                  <div key={i} className="flex items-center justify-between bg-gray-50 rounded-md px-3 py-1.5 text-xs">
                    <span className="text-gray-700 truncate flex-1">{att.filename}</span>
                    <span className="text-[9px] text-gray-400 mx-2">{att.uploaded_at?.split('T')[0]}</span>
                    <button onClick={async () => { try { const blob = await fileAPI.download(att.storage_path); window.open(URL.createObjectURL(blob)); } catch { alert('Download failed'); } }} className="text-blue-600 hover:underline text-[10px]">View</button>
                  </div>
                ))}
              </div>
            ) : <p className="text-[10px] text-gray-400">No files attached. Upload invoices, packing lists, BOL, etc.</p>}
          </div>

          {/* Shipment Updates Section */}
          <div className="border border-gray-100 rounded-lg p-4" data-testid="po-updates-section">
            <div className="flex items-center gap-2 mb-3">
              <MessageSquare className="w-4 h-4" style={{ color: '#24549c' }} />
              <h3 className="text-xs font-bold text-gray-900">Shipment Updates</h3>
            </div>

            {/* Add Update Input */}
            <div className="flex gap-2 mb-4">
              <input
                data-testid="po-update-input"
                value={updateText}
                onChange={e => setUpdateText(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleAddUpdate()}
                placeholder="Add an update about this shipment (e.g., BL received, vessel sailed, at CFS)..."
                className="flex-1 px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200"
              />
              <button
                data-testid="po-update-submit"
                onClick={handleAddUpdate}
                disabled={submittingUpdate || !updateText.trim()}
                className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-white rounded-lg disabled:opacity-50 hover:shadow-md transition-all"
                style={{ backgroundColor: '#24549c' }}
              >
                <Send className="w-3 h-3" /> Save
              </button>
            </div>

            {/* Update History */}
            <div>
              <div className="flex items-center gap-1.5 mb-2">
                <Clock className="w-3 h-3 text-gray-400" />
                <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Update History</p>
              </div>
              {updates.length > 0 ? (
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {[...updates].reverse().map((upd, idx) => {
                    const dt = new Date(upd.created_at);
                    const dateStr = dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
                    const timeStr = dt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
                    return (
                      <div key={upd.id || idx} data-testid={`po-update-${idx}`} className="flex gap-3 items-start">
                        <div className="flex flex-col items-center mt-1">
                          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#24549c' }} />
                          {idx < updates.length - 1 && <div className="w-px h-full bg-gray-200 mt-1" />}
                        </div>
                        <div className="flex-1 bg-gray-50 rounded-lg p-3">
                          <p className="text-xs text-gray-800">{upd.text}</p>
                          <div className="flex items-center gap-2 mt-1.5">
                            <span className="text-[9px] text-gray-400">{dateStr} at {timeStr}</span>
                            <span className="text-[9px] text-gray-400">by {upd.created_by_name || 'System'}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-[10px] text-gray-400 text-center py-3">No updates yet. Add your first shipment update above.</p>
              )}
            </div>
          </div>

          {/* Revision History */}
          <RevisionHistory docType="purchase_order" docId={selectedPO.id} />

          {/* Status Dropdown + Confirm + Cancel */}
          <div className="flex gap-2 pt-3 border-t border-gray-100">
            {['Ordered', 'In Transit'].includes(selectedPO.status) ? (
              <select data-testid="po-status-select" value={selectedPO.status} onChange={e => updatePOStatus(selectedPO.id, e.target.value)} className="flex-1 px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200">
                {selectedPO.status === 'Ordered' && (
                  <>
                    <option value="Ordered">Ordered</option>
                    <option value="In Transit">In Transit</option>
                  </>
                )}
                {selectedPO.status === 'In Transit' && (
                  <>
                    <option value="In Transit">In Transit</option>
                  </>
                )}
              </select>
            ) : (
              <div className="flex-1 px-3 py-2 text-xs text-gray-500 bg-gray-50 rounded-lg border border-gray-200">
                Status: <span className="font-semibold text-gray-700">{selectedPO.status}</span>
                {selectedPO.status === 'Draft' && <span className="ml-2 text-[10px] text-gray-500">(awaiting manager approval)</span>}
                {selectedPO.status === 'Partially Received' && <span className="ml-2 text-[10px] text-amber-600">(auto-managed by Inward)</span>}
                {selectedPO.status === 'Received' && <span className="ml-2 text-[10px] text-green-600">(fully received)</span>}
                {selectedPO.status === 'Cancelled' && <span className="ml-2 text-[10px] text-red-600">(cancelled)</span>}
              </div>
            )}
            {selectedPO.status === 'Draft' && canConfirmPO && (
              <button
                data-testid="confirm-po-btn"
                onClick={() => confirmDraftPO(selectedPO.id)}
                className="flex items-center gap-1 px-3 py-2 text-xs font-semibold text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors"
              >
                ✓ Confirm PO
              </button>
            )}
            {['Draft', 'Ordered', 'In Transit', 'Received', 'Partially Received'].includes(selectedPO.status) && (
              <button
                data-testid="cancel-po-btn"
                onClick={async () => {
                  const msg = ['Received', 'Partially Received'].includes(selectedPO.status)
                    ? 'Cancel this Purchase Order? Allowed only if all inwards are already cancelled (no stock linked). If linked to an indent, the indent will be reverted to Approved.'
                    : 'Cancel this Purchase Order? If linked to an indent, the indent will be reverted to Approved.';
                  if (!window.confirm(msg)) return;
                  try {
                    const updated = await purchaseAPI.cancel(selectedPO.id);
                    setSelectedPO(updated);
                    toast.success('Purchase Order cancelled successfully');
                    fetchAll();
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Failed to cancel PO');
                  }
                }}
                className="flex items-center gap-1 px-3 py-2 text-xs font-semibold text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
              >
                Cancel PO
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PurchaseOrderDetail;
